import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useMutation } from '@apollo/client'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { t } from '../../../utils'
import { labCommunityLeaderboardPath } from '../../../utils/routeHelpers'
import {
  COMMUNITY_PROFILE_QUERY,
  MARK_LEVEL_CELEBRATED_MUTATION
} from '../../../gql'
import { getLevelConfig } from '../../../utils/levelsConfig'
import LabModal from './LabModal'
import LevelUpShield from './LevelUpShield'
import styles from './_levelUpModal.module.scss'

const LevelUpConfetti = dynamic(() => import('./LevelUpConfetti'), {
  ssr: false
})

interface LevelUpModalProps {
  userId: number
  level: number
  allVotesCount?: number
  closeModal: () => void
}

const LevelUpModal: React.FC<LevelUpModalProps> = ({
  userId,
  level,
  allVotesCount = 0,
  closeModal
}) => {
  const router = useRouter()
  const levelConfig = getLevelConfig(level)

  const [markLevelCelebrated] = useMutation(MARK_LEVEL_CELEBRATED_MUTATION, {
    variables: { level },
    refetchQueries: [
      {
        query: COMMUNITY_PROFILE_QUERY,
        variables: { userId }
      }
    ]
  })

  const dismissModal = async () => {
    try {
      await markLevelCelebrated()
    } catch {
      // Allow dismissal even if marking celebrated fails; it can retry on next visit.
    } finally {
      closeModal()
    }
  }

  const handleContinue = async () => {
    await dismissModal()
    router.push(labCommunityLeaderboardPath())
  }

  if (!levelConfig) {
    return null
  }

  return (
    <div className={styles.levelUpModal}>
      <LabModal
        onClose={dismissModal}
        overlayStyle={styles.overlay}
        contentContainerStyle={styles.modalContentContainer}
        isCloseButtonHidden
      >
        <div className={styles.dialog}>
          <button
            type="button"
            className={styles.closeButton}
            onClick={dismissModal}
            aria-label={t('Close')}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>

          <div className={styles.modal}>
            <LevelUpConfetti />

            <div className={styles.topSection}>
              <div className={styles.topContent}>
                <h2 className={styles.title}>{t('Congratulations!')}</h2>
                <div className={styles.summary}>
                  <p className={styles.summaryLead}>
                    {t('People are loving your posts!')}
                  </p>
                  <p className={styles.summaryDetail}>
                    {level === 1 ? (
                      t("You've reached level 1")
                    ) : (
                      <>
                        {t("You've gotten")} {allVotesCount.toLocaleString()}{' '}
                        {t('votes and reached level')} {level}!
                      </>
                    )}
                  </p>
                </div>
              </div>{' '}
            </div>
            <div className={styles.midSection}>
              <div className={styles.shieldOuter}>
                <LevelUpShield level={level} part="outer" />
              </div>

              <div className={styles.bottomSection}>
                <h3 className={styles.levelName}>{levelConfig.name}</h3>

                <button
                  type="button"
                  className={styles.continueButton}
                  onClick={handleContinue}
                >
                  {t('View Leaderboard')}
                </button>
              </div>

              <div className={styles.shieldInner}>
                <LevelUpShield level={level} part="inner" />
              </div>
            </div>
          </div>
        </div>
      </LabModal>
    </div>
  )
}

export default LevelUpModal
